<template>
	<Card :title="trans('enrollments.purchase-orders.receipts.title')" :subtitle="trans('enrollments.purchase-orders.receipts.description')">
		<Alert v-if="!enrollment.registration_stage.refundable" :color="Color.Blue">{{ trans('enrollments.not_refundable') }}</Alert>
		<LabeledObject>
			<template #label>{{ trans('enrollments.purchase-orders.amount_owed') }}</template>
			<p>$ {{ formatNumber(purchaseOrder.price, 2, trans('general.number.decimal'), trans('general.number.thousands')) }}</p>
		</LabeledObject>
		<LabeledObject v-if="purchaseOrder.payment_deadline">
			<template #label>{{ trans('enrollments.purchase-orders.deadline') }}</template>
			<p>{{ dayjs(purchaseOrder.payment_deadline).format(trans('general.dayjs.datetime')) }}</p>
		</LabeledObject>
		<div v-if="purchaseOrder.is_awaiting || purchaseOrder.is_pending" class="flex flex-col md:flex-row items-stretch md:items-end gap-3">
			<LabeledObject required full>
				<template #label>{{ trans('general.select_file') }}</template>
				<input type="file" name="file" class="grow" accept="application/pdf,image/jpeg,image/png" @change="loadFile($event)">
			</LabeledObject>
			<PrimaryButton :color="Color.Green" @click.stop="upload">
				<i class="fa-solid fa-upload"/>
			</PrimaryButton>
		</div>
		<List v-if="receipts.length > 0">
			<ListItem v-for="receipt in receipts" :title="dayjs(receipt.created_at).format(trans('general.dayjs.datetime'))">
				<a :href="route('events.enrollments.purchase-orders.receipts.show', [enrollment.id, purchaseOrder.id, receipt.id])" target="_blank">
					<SecondaryButton :color="Color.Blue">
						<i class="fa-solid fa-eye mr-2"/>
						{{ trans('enrollments.purchase-orders.receipts.show') }}
					</SecondaryButton>
				</a>
			</ListItem>
		</List>
	</Card>
</template>
<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/Cards/Card.vue'
import List from '@/Components/Lists/List.vue'
import {defineProps, type Ref, ref} from 'vue'
import Alert from '@/Components/Alerts/Alert.vue'
import ListItem from '@/Components/Lists/ListItem.vue'
import LabeledObject from '@/Components/LabeledObject.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import {type Enrollment} from '@/Types/Events/Enrollments/Enrollment'
import {formatNumber, showInfo, showSuccess, showValidationErrors} from '@/helpers'
import SecondaryButton from '@/Components/Buttons/SecondaryButton.vue'
import {type PurchaseOrder} from '@/Types/Events/Enrollments/PurchaseOrders/PurchaseOrder'
import {type PaymentReceipt} from '@/Types/Events/Enrollments/PurchaseOrders/PaymentReceipt'

const props = defineProps<{
	enrollment:Enrollment,
	purchaseOrder:PurchaseOrder,
	receipts:Array<PaymentReceipt>,
}>()

const working:Ref<boolean> = ref(false)
const enrollment:Ref<Enrollment> = ref(props.enrollment)
const receipts:Ref<Array<PaymentReceipt>> = ref(props.receipts)
const purchaseOrder:Ref<PurchaseOrder> = ref(props.purchaseOrder)
const newReceipt:Ref<PaymentReceipt> = ref({
	file: null,
} as PaymentReceipt)

function loadFile(event: Event):void{
	if(event.target && event.target instanceof HTMLInputElement && event.target.files){
		newReceipt.value.file = event.target.files[0]
	}
}

function upload():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.uploading_file'))
		const formData = new FormData
		formData.append('file', newReceipt.value.file ?? '')
		axios.post(route('events.enrollments.purchase-orders.receipts.store', [enrollment.value.id, purchaseOrder.value.id]), formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}).then(({data}:{data:{purchaseOrder:PurchaseOrder, paymentReceipt:PaymentReceipt}}) => {
			showSuccess(trans('general.inserted'))
			receipts.value.unshift(data.paymentReceipt)
			purchaseOrder.value = data.purchaseOrder
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>